import { Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WebPageBlockType } from '../../../../api/types';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import { type TextImageBlockInput, type WebpageFormikValue } from '../shared';
import { colSpan } from './ColumnListBlock';
import ReorderingButtons, { getNewBlock } from './shared';

type TextImageBlockProps = {
    name: string;
    index: number;
    disabled?: boolean;
};
const TextImageBlock = ({ name, index, disabled = false }: TextImageBlockProps) => {
    const { t } = useTranslation('webpageDetails');
    const { webpageBlockPosition, webPageBlockBackgroundOptions, webpageBlockType } = useSystemOptions();
    const { yesNoSwitch } = useSystemSwitchData();
    const { values, setFieldValue } = useFormikContext<WebpageFormikValue>();
    const onSelect = useCallback(
        (value, options) => {
            values.webPageBlock[index] = getNewBlock(value);
            setFieldValue(name, getNewBlock(value));
        },
        [index, name, setFieldValue, values.webPageBlock]
    );

    const textImageBlock: TextImageBlockInput = useMemo(
        () =>
            values.webPageBlock[index].type === WebPageBlockType.TextImage &&
            (values.webPageBlock[index] as TextImageBlockInput),
        [index, values.webPageBlock]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('webpageDetails:fields.textImage.blockName')}>
                <Panel
                    key={t('webpageDetails:fields.textImage.blockName')}
                    extra={
                        !disabled && <ReorderingButtons index={index} setFieldValue={setFieldValue} values={values} />
                    }
                    header={t('webpageDetails:fields.textImage.blockName')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('webpageDetails:fields.blockType', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.type`}
                                onSelect={onSelect}
                                options={webpageBlockType}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('webpageDetails:fields.textImage.backgroundType', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={`${name}.backgroundType`}
                                options={webPageBlockBackgroundOptions}
                                showSearch
                            />
                        </Col>

                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('webpageDetails:fields.textImage.imagePosition', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={`${name}.imagePosition`}
                                options={webpageBlockPosition}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('webpageDetails:fields.textImage.image', { returnObjects: true })}
                                disabled={disabled}
                                extensions={[...allowedExtensions.image, ...allowedExtensions.video]}
                                name={`${name}.image`}
                                sizeLimitInMiB={20}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.textImage.title', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.title`}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('webpageDetails:fields.textImage.description', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={`${name}.description`}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('webpageDetails:fields.textImage.button.isActive', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={`${name}.button.isActive`}
                                {...yesNoSwitch}
                            />
                        </Col>
                        {!isNil(textImageBlock?.button.isActive) && textImageBlock?.button.isActive && (
                            <>
                                <Col {...colSpan}>
                                    <FormFields.TranslatedInputField
                                        {...t('webpageDetails:fields.textImage.button.text', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}.button.text`}
                                    />
                                </Col>
                                <Col {...colSpan}>
                                    <FormFields.InputField
                                        {...t('webpageDetails:fields.textImage.button.url', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}.button.url`}
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default TextImageBlock;
